import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import PerformanceDocTableRow from './PerformanceDocTableRow'
import PerformanceDocsAddPopup from '../PerformanceDocsAddPopup'
import PerformanceDocsEditPopup from '../PerformanceDocsEditPopup'

import PerformancePaymentTableRow from './PerformancePaymentTableRow'
import PerformancePaymentsAddPopup from '../PerformancePaymentsAddPopup'

import { ContractPosition } from '../../../../types'

import {
    ContractPerformanceDoc,
    PerformanceContractDocPositionDraft,
    PerformanceContractDocPositionEdit,
    ContractPerformancePayment,
    ContractPerformancePaymentInit,
    PerformancePaymentsError,
} from '../../types'

import LoadingIcon from '../../../../../../../components/LoadingIcon'

import { CurrencyList } from '../../../types'
import HoverTooltip from '../../../../../../../components/HoverTooltip'

interface PerformanceCurrency {
    currencyCharCode: string | null
    currencyName: string | null
    currencyDigitalCode: number | null
}

const initialPayment: ContractPerformancePaymentInit = {
    payment: {
        guid: null,
        ordinal: 1,
        payment_date: null,
        price: 0,
        currency_code: null,
        currency_digital_code: null,
        currency_name: null,
        currency_rate: 0,
        rub_price: 0,
        prepaid: false,
        annotation: null,
        performance_contract_guid: null,
        performance_contract_position_guid: null,
    },
    acceptances: [],
}

type PerfTotalPaymentSum = {
    total_payment: number
    total_payment_rub: number
}

type Props = {
    theme: string
    perfAction: boolean
    performanceGuid: string | undefined
    contractGuid: string | undefined
    contractRegNumber: string | undefined
    currencyList: CurrencyList[]
    performanceCurrency?: PerformanceCurrency
    onTotalChange: (total: number) => void
    onPerformancePayments: (have_perf_payments: boolean) => void
    errors?: PerformancePaymentsError
    onAnyEdit: () => void
}

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(2) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

const PerformancePaymentTable: React.FC<Props> = (props) => {
    const navigate = useNavigate()

    const makeInitialPayment = (
        currency?: PerformanceCurrency
    ): ContractPerformancePaymentInit => ({
        payment: {
            guid: null,
            ordinal: 1,
            payment_date: null,
            price: 0,
            currency_code: currency?.currencyCharCode ?? null,
            currency_digital_code: currency?.currencyDigitalCode ?? null,
            currency_name: currency?.currencyName ?? null,
            currency_rate: null,
            rub_price: 0,
            prepaid: false,
            annotation: null,
            performance_contract_guid: null,
            performance_contract_position_guid: null,
        },
        acceptances: [],
    })

    const [performancePayments, setPerformancePayments] = useState<
        ContractPerformancePayment[] | null
    >(null)
    const [isLoadingPerformancePayments, setLoadingPerformancePayments] =
        useState(false)

    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [performanceTotalPaymentSum, setPerformanceTotalPaymentSum] =
        useState<PerfTotalPaymentSum | null>()
    const [
        isLoadingPerformanceTotalPaymentSum,
        setLoadingPerformanceTotalPaymentSum,
    ] = useState(false)

    const [performancePaymentsAddPopup, setPerformancePaymentsAddPopup] =
        useState<boolean>(false)
    const [performancePaymentsEditPopup, setPerformancePaymentsEditPopup] =
        useState<boolean>(false)

    const [deletePerformancePositionPopup, setDeletePerformancePositionPopup] =
        useState<boolean>(false)
    const [isDeleting, setDeleting] = useState<boolean>(false)

    const [
        deleteRowPerformancePositionPopup,
        setDeleteRowPerformancePositionPopup,
    ] = useState<boolean>(false)
    const [deleteRowGuid, setDeleteRowGuid] = useState<string>('')

    const [isSavingPerformancePay, setIsSavingPerformancePay] = useState(false)

    const [
        isLoadingPerformancePaymentsPosition,
        setLoadingPerformancePaymentsPosition,
    ] = useState(false)
    const [
        performanceContractPaymentsPosition,
        setPerformanceContractPaymentsPosition,
    ] = useState<ContractPerformancePaymentInit>(
        makeInitialPayment(props.performanceCurrency)
    )

    const loadPerformanceContractPayments = () => {
        setLoadingPerformancePayments(true)

        fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v1/performance_contract_payment/performance_contract_guid/${props.performanceGuid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    props.onPerformancePayments(false)
                    throw new Error('Failed to fetch count')
                }
            })
            .then((data) => {
                // Обновляем состояния
                setPerformancePayments(data)
                props.onPerformancePayments(true)
            })
            .catch((e) => {
                console.error(e)
                // Устанавливаем значения по умолчанию на случай ошибки
                props.onPerformancePayments(false)
                setPerformancePayments(null)
            })
            .finally(() => {
                setLoadingPerformancePayments(false)
            })
    }

    // Функция для обработки изменения состояния отдельного checkbox
    const handleCheckboxChange = (perfDoc_position_guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(perfDoc_position_guid)) {
                return prevSelectedItems.filter(
                    (itemId) => itemId !== perfDoc_position_guid
                )
            } else {
                return [...prevSelectedItems, perfDoc_position_guid]
            }
        })
    }

    // Функция для обработки изменения состояния отдельного checkbox при нажатии правой кнопкой
    const handleRightClickCheckboxChange = (perfDoc_position_guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(perfDoc_position_guid)) {
                return prevSelectedItems // Если уже выбран, ничего не меняем
            }
            return [...prevSelectedItems, perfDoc_position_guid] // Добавляем в выбранные
        })
    }

    // Функция для обработки изменения состояния "Select All"
    // const handleSelectAllChange = () => {
    //     setSelectAll(!selectAll)
    //     if (!selectAll) {
    //         // Выбираем только те элементы, которые соответствуют текущей стадии
    //         const filteredItems = performanceDocs
    //             ? performanceDocs.map(
    //                   (item) => item.performance_contract_position_guid
    //               )
    //             : []

    //         setSelectedItems(filteredItems)
    //     } else {
    //         // Сбрасываем выбор всех элементов
    //         setSelectedItems([])
    //     }
    // }

    const loadPerformanceTotalPaymentSum = async () => {
        setLoadingPerformanceTotalPaymentSum(true)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_payment/performance_contract_guid/total_payment/${props.performanceGuid}`
        )
        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            })
            if (!response.ok) {
                setPerformanceTotalPaymentSum(null)
                throw new Error(
                    `Error: ${response.status} ${response.statusText}`
                )
            }
            const json: PerfTotalPaymentSum = await response.json()
            setPerformanceTotalPaymentSum(json)
        } catch (e) {
            console.error('Error fetching payment sum:', e)
            setPerformanceTotalPaymentSum(null)
        } finally {
            setLoadingPerformanceTotalPaymentSum(false)
        }
    }

    const loadPerformanceContractPaymentsPosition = async (
        performance_contract_payment_guid: string
    ) => {
        setLoadingPerformancePaymentsPosition(true)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_payment_with_acceptances?performance_contract_guid=${props.performanceGuid}&performance_contract_payment_guid=${performance_contract_payment_guid}`
        )
        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            })
            if (!response.ok) {
                setPerformanceContractPaymentsPosition(initialPayment)
                throw new Error(
                    `Error: ${response.status} ${response.statusText}`
                )
            }
            const json: ContractPerformancePaymentInit = await response.json()
            setPerformanceContractPaymentsPosition(json)
        } catch (e) {
            console.error('Error fetching payment sum:', e)
            setPerformanceContractPaymentsPosition(initialPayment)
        } finally {
            setLoadingPerformancePaymentsPosition(false)
        }
    }

    const deletePerformanceContractPosition = async (guid: string) => {
        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_position/${guid}`

        const res = await fetch(url, {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })

        if (!res.ok) {
            // Пытаемся прочитать сообщение об ошибке
            let errText = ''
            try {
                const data = await res.json()
                errText = data?.detail || data?.message || JSON.stringify(data)
            } catch {
                try {
                    errText = await res.text()
                } catch {
                    // no-op
                }
            }
            throw new Error(
                `DELETE ${guid} failed: ${res.status} ${res.statusText}${errText ? ' — ' + errText : ''}`
            )
        }
    }

    const handleDeletePerformancePositionPopup = () => {
        setDeletePerformancePositionPopup(!deletePerformancePositionPopup)
    }

    const handleDeleteRowPerformancePositionPopup = () => {
        setDeleteRowPerformancePositionPopup(!deleteRowPerformancePositionPopup)
    }

    const handleDeleteClick = async () => {
        setDeleting(true)

        try {
            if (!selectedItems.length) {
                // Нечего удалять
                return
            }

            // Параллельно отправляем DELETE по каждому guid
            const results = await Promise.allSettled(
                selectedItems.map((guid) =>
                    deletePerformanceContractPosition(guid)
                )
            )

            // Разбираем успехи/ошибки
            const failed: { guid: string; error: string }[] = []
            results.forEach((r, idx) => {
                const guid = selectedItems[idx]
                if (r.status === 'rejected') {
                    const msg =
                        r.reason instanceof Error
                            ? r.reason.message
                            : String(r.reason)
                    failed.push({ guid, error: msg })
                }
            })

            // В любом случае обновляем данные
            await Promise.allSettled([
                loadPerformanceContractPayments(),
                loadPerformanceTotalPaymentSum(),
            ])
        } finally {
            setDeleting(false)
            setSelectedItems([])
            setSelectAll(false)
            setDeletePerformancePositionPopup(false)
            setDeleteRowPerformancePositionPopup(false)
            props.onAnyEdit()
        }
    }

    const createPerformanceContractPayment = async (
        payload: ContractPerformancePaymentInit
    ) => {
        setIsSavingPerformancePay(true)

        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_payment_with_acceptances?performance_contract_guid=${props.performanceGuid}`

        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            })

            if (response.ok) {
                console.log('Документ успешно создан')
                loadPerformanceContractPayments()
                loadPerformanceTotalPaymentSum()
            } else {
                console.log('Ошибка! Документ не создан')
            }
        } catch (e) {
            console.log('Ошибка! Документ не создан')
        } finally {
            setIsSavingPerformancePay(false)
            props.onAnyEdit()
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (props.performanceGuid) {
                    loadPerformanceContractPayments()
                    loadPerformanceTotalPaymentSum()
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    useEffect(() => {
        props.onTotalChange(
            performanceTotalPaymentSum?.total_payment_rub
                ? performanceTotalPaymentSum?.total_payment_rub
                : 0
        )
    }, [performanceTotalPaymentSum])

    const n = (v: unknown, def = 0) =>
        v === null || v === undefined || v === '' ? def : Number(v)

    const isRUB = (digital?: number | null) => Number(digital) === 643

    const reindexOrdinals = <T extends { ordinal: number }>(arr: T[]): T[] =>
        arr.map((item, idx) => ({ ...item, ordinal: idx + 1 }))

    const handlePerformancePaymentsAddPopup = () => {
        setPerformanceContractPaymentsPosition(
            makeInitialPayment(props.performanceCurrency)
        )
        setPerformancePaymentsAddPopup(!performancePaymentsAddPopup)
    }

    const handlePerformancePaymentsEditPopup = (
        performance_contract_payment_guid: string
    ) => {
        const willOpen = !performancePaymentsEditPopup
        setPerformancePaymentsAddPopup(willOpen)
        if (willOpen) {
            loadPerformanceContractPaymentsPosition(
                performance_contract_payment_guid
            )
        } else {
            setPerformanceContractPaymentsPosition(
                makeInitialPayment(props.performanceCurrency)
            )
        }
    }

    return (
        <>
            <fieldset disabled={false}>
                <HoverTooltip
                    tooltipText={props.errors?.have_payments_items || ''}
                >
                    <div
                        className={`flex flex-col w-full min-w-fit bg-[#e4e6e8] dark:bg-[#242225] rounded ${
                            props.errors?.have_payments_items
                                ? 'border border-[#C15C5C]'
                                : ''
                        }`}
                    >
                        {/* toolbar */}
                        <div className="flex flex-row gap-1 p-[5px] h-[40px] text-[10px] rounded-t text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                            {props.perfAction ? (
                                ''
                            ) : (
                                <>
                                    <div className="flex w-8 h-full items-center justify-center text-center">
                                        {/* Добавить оплату*/}
                                        <button
                                            onClick={
                                                handlePerformancePaymentsAddPopup
                                            }
                                            className="w-6 h-6 grid rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                        >
                                            <HoverTooltip tooltipText="Добавить оплату">
                                                <svg
                                                    className="w-4 h-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    id="plus"
                                                >
                                                    <path
                                                        fill={
                                                            props.theme ==
                                                            'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                        }
                                                        d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                                    />
                                                </svg>
                                            </HoverTooltip>
                                        </button>
                                    </div>
                                    <div className="flex w-8 h-full items-center justify-center text-center">
                                        {/* Удалить */}
                                        <button
                                            onClick={
                                                handleDeletePerformancePositionPopup
                                            }
                                            className={`flex w-6 h-6 rounded-full items-center justify-center ${selectedItems.length > 0 ? 'hover:bg-[#FFFFFF] dark:hover:bg-[#656365]' : ''} `}
                                            disabled={
                                                selectedItems.length > 0
                                                    ? false
                                                    : true
                                            }
                                        >
                                            <HoverTooltip tooltipText="Удалить документ">
                                                <svg
                                                    className="w-4 h-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    id="plus"
                                                >
                                                    <path
                                                        fill={`${
                                                            selectedItems.length >
                                                            0
                                                                ? props.theme ==
                                                                  'dark'
                                                                    ? '#FFFFFF'
                                                                    : '#181818'
                                                                : '#8A8A8A'
                                                        } `}
                                                        d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                    ></path>
                                                </svg>
                                            </HoverTooltip>
                                        </button>
                                    </div>
                                </>
                            )}

                            <div className="flex flex-1 h-full items-center justify-center text-center"></div>
                            <div className="flex flex-col w-[215px] h-full gap-1 items-center justify-center text-center">
                                <div className="flex w-full h-fit items-center justify-end text-right">
                                    Сумма оплаты (₽)
                                    <br />
                                    {isLoadingPerformanceTotalPaymentSum ? (
                                        <div className="flex w-8 items-center justify-center text-center text-[#8A8A8A] ">
                                            <svg
                                                className={`animate-spin h-4 w-4 ${
                                                    props.theme === 'dark'
                                                        ? 'text-[#FFFFFF]'
                                                        : 'text-[#181818]'
                                                }`}
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                ></path>
                                            </svg>
                                        </div>
                                    ) : performanceTotalPaymentSum?.total_payment_rub ? (
                                        Number(
                                            performanceTotalPaymentSum?.total_payment_rub
                                        ).toLocaleString('ru-RU', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 13,
                                        })
                                    ) : (
                                        performanceTotalPaymentSum?.total_payment
                                    )}
                                </div>
                            </div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        </div>
                        {/* name */}
                        <div className="flex flex-row gap-1 h-[40px] p-[5px] items-center justify-center text-[11px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-[40px] h-full items-center justify-center text-center">
                                №
                            </div>
                            <div className="flex flex-1 h-full items-center justify-center text-center">
                                Наименование документа
                            </div>
                            <div className="flex flex-col w-[215px] h-full items-center justify-center text-center">
                                <div className="flex flex-row w-[215px] h-[25px] items-center justify-center text-center">
                                    <div className="flex w-[95px] h-full items-center justify-center text-center">
                                        Дата
                                    </div>
                                    <div className="flex w-[120px] h-full items-center justify-center text-center">
                                        Сумма
                                    </div>
                                </div>
                            </div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        </div>

                        {/* date */}
                        <div className="flex flex-col w-full gap-1 my-1 overflow-y custom-scrollbar">
                            {isLoadingPerformancePayments ? (
                                <div className="flex w-full h-full items-center justify-center">
                                    <LoadingIcon />
                                </div>
                            ) : performancePayments ? (
                                performancePayments.map((row, index) => (
                                    <PerformancePaymentTableRow
                                        key={row.unic_code}
                                        perfAction={props.perfAction}
                                        performanceGuid={props.performanceGuid}
                                        perfPaymentNumber={index + 1}
                                        theme={props.theme}
                                        performancePayment={row}
                                        selected={selectedItems.includes(
                                            row.performance_contract_position_guid
                                        )}
                                        onClick={(perfPayment_guid) => {
                                            handleCheckboxChange(
                                                perfPayment_guid
                                            )
                                        }}
                                        onRightClick={(perfPayment_guid) => {
                                            handleRightClickCheckboxChange(
                                                perfPayment_guid
                                            )
                                        }}
                                        onClickEdit={(perfPayment_guid) =>
                                            handlePerformancePaymentsEditPopup(
                                                perfPayment_guid
                                            )
                                        }
                                        onDeleteRow={(perfPayment_guid) => {
                                            setSelectedItems([])
                                            setDeleteRowGuid(perfPayment_guid)
                                            handleDeleteRowPerformancePositionPopup()
                                        }}
                                    />
                                ))
                            ) : (
                                <div className="flex w-full h-full py-1 items-center justify-center text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                    Нет данных
                                </div>
                            )}
                        </div>
                        {/* buttons */}
                        <div className="flex flex-row gap-1 h-[40px] text-[10px] p-1 rounded-b text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#333033]">
                            <div className="flex flex-1 flex-row gap-3 px-5 h-full items-center justify-start text-center">
                                {!props.perfAction && (
                                    <>
                                        <button
                                            className="flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                            disabled={false}
                                            onClick={
                                                handlePerformancePaymentsAddPopup
                                            }
                                        >
                                            <svg
                                                className="w-3 h-3"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 22 22"
                                                id="plus"
                                            >
                                                <path
                                                    fill={`${props.theme == 'dark' ? '#FFFFFF' : '#181818'}`}
                                                    d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                                ></path>
                                            </svg>
                                            Добавить
                                        </button>
                                        <button
                                            className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs ${selectedItems.length > 0 ? 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] hover:bg-[#ed9cae] dark:hover:bg-[#701f1f] bg-[#f6f6f6] dark:bg-[#494849]' : 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#f6f6f6] dark:bg-[#494849]'} `}
                                            disabled={
                                                !(selectedItems.length > 0)
                                            }
                                            onClick={
                                                handleDeletePerformancePositionPopup
                                            }
                                        >
                                            <svg
                                                className="w-3 h-3"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 22 22"
                                                id="trash"
                                            >
                                                <path
                                                    fill={
                                                        selectedItems.length > 0
                                                            ? props.theme ==
                                                              'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                            : props.theme ==
                                                                'dark'
                                                              ? '#8A8A8A'
                                                              : '#6A6A6A'
                                                    }
                                                    d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                ></path>
                                            </svg>
                                            Удалить
                                        </button>
                                    </>
                                )}
                            </div>
                            <div className="flex flex-1 h-full items-center justify-center text-center"></div>
                            <div className="flex flex-col w-[215px] h-full gap-1 items-center justify-center text-center">
                                <div className="flex w-full h-fit items-center justify-end text-right">
                                    Сумма оплаты (₽)
                                    <br />
                                    {isLoadingPerformanceTotalPaymentSum ? (
                                        <div className="flex w-8 items-center justify-center text-center text-[#8A8A8A] ">
                                            <svg
                                                className={`animate-spin h-4 w-4 ${
                                                    props.theme === 'dark'
                                                        ? 'text-[#FFFFFF]'
                                                        : 'text-[#181818]'
                                                }`}
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                ></path>
                                            </svg>
                                        </div>
                                    ) : performanceTotalPaymentSum?.total_payment_rub ? (
                                        Number(
                                            performanceTotalPaymentSum?.total_payment_rub
                                        ).toLocaleString('ru-RU', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 13,
                                        })
                                    ) : (
                                        performanceTotalPaymentSum?.total_payment
                                    )}
                                </div>
                            </div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        </div>
                    </div>
                </HoverTooltip>
            </fieldset>

            {performancePaymentsAddPopup && (
                <PerformancePaymentsAddPopup
                    theme={props.theme}
                    performanceGuid={props.performanceGuid}
                    currencyList={props.currencyList}
                    handlePerformancePaymentsAddPopup={
                        handlePerformancePaymentsAddPopup
                    }
                    performanceContractPaymentsPosition={
                        performanceContractPaymentsPosition
                    }
                    contractGuid={props.contractGuid}
                    contractRegNumber={props.contractRegNumber}
                    isLoadingPerformancePaymentsPosition={
                        isLoadingPerformancePaymentsPosition
                    }
                    isSavingPerformancePay={isSavingPerformancePay}
                    onPrepaid={(pay_prepaid) => {
                        setPerformanceContractPaymentsPosition((prev) => ({
                            ...prev,
                            payment: {
                                ...prev.payment,
                                prepaid: pay_prepaid,
                            },
                        }))
                    }}
                    onPayNumber={(pay_number) => {
                        setPerformanceContractPaymentsPosition((prev) => ({
                            ...prev,
                            payment: {
                                ...prev.payment,
                                annotation: pay_number,
                            },
                        }))
                    }}
                    onPayDate={(pay_date) => {
                        setPerformanceContractPaymentsPosition((prev) => ({
                            ...prev,
                            payment: {
                                ...prev.payment,
                                payment_date: pay_date,
                            },
                        }))
                    }}
                    onPayPrice={(pay_price) => {
                        setPerformanceContractPaymentsPosition((prev) => ({
                            ...prev,
                            payment: {
                                ...prev.payment,
                                price: pay_price,
                            },
                        }))
                    }}
                    onPayRubPrice={(pay_rub_price) => {
                        setPerformanceContractPaymentsPosition((prev) => ({
                            ...prev,
                            payment: {
                                ...prev.payment,
                                rub_price: pay_rub_price,
                            },
                        }))
                    }}
                    onChangeCurrency={(currency) => {
                        setPerformanceContractPaymentsPosition((prev) => ({
                            ...prev,
                            payment: {
                                ...prev.payment,
                                currency_code: currency.code,
                                currency_digital_code: currency.digital_code,
                                currency_name: currency.name,
                            },
                        }))
                    }}
                    onChangeCurrencyRate={(pay_currency_rate) => {
                        setPerformanceContractPaymentsPosition((prev) => ({
                            ...prev,
                            payment: {
                                ...prev.payment,
                                currency_rate: pay_currency_rate,
                            },
                        }))
                    }}
                    onAddPay={(data) => {
                        createPerformanceContractPayment(data)
                    }}
                    onChangeAcceptances={(acceptances) => {
                        setPerformanceContractPaymentsPosition((prev) => ({
                            ...prev,
                            acceptances,
                        }))
                    }}
                />
            )}

            {deletePerformancePositionPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center z-50">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Удалить документы?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleDeletePerformancePositionPopup
                                    }
                                    disabled={isDeleting ? true : false}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={handleDeleteClick}
                                >
                                    {isDeleting ? (
                                        <svg
                                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                            ></path>
                                        </svg>
                                    ) : (
                                        'Удалить'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {deleteRowPerformancePositionPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center z-50">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Удалить документ?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleDeleteRowPerformancePositionPopup
                                    }
                                    disabled={isDeleting ? true : false}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={handleDeleteClick}
                                >
                                    {isDeleting ? (
                                        <svg
                                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                            ></path>
                                        </svg>
                                    ) : (
                                        'Удалить'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default PerformancePaymentTable
